import classNames from 'classnames';
import RcTooltip, { RCTooltip as RCTooltipType } from 'rc-tooltip';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { BottomSheet } from 'react-spring-bottom-sheet';

import { useBowser } from 'common/hooks';
import svgIcons from 'common/svgIcons';

import SvgIconUrl from '../SvgIconUrl/SvgIconUrl.component';
import './tooltip.css';
import { AlignType } from './types';

export type TooltipPlacementType = RCTooltipType.Props['placement'];
export type TooltipOverlayType = RCTooltipType.Props['overlay'];
export type TooltipTriggerType = RCTooltipType.Props['trigger'];
export type TooltipAlign = AlignType;

const defaultTrigger: TooltipTriggerType = ['hover'];

type TooltipProps = Omit<RCTooltipType.Props, 'overlay'> & {
  enabled: boolean;
  overlay: React.ReactNode;
  overlayTitle?: string;
  align?: AlignType | undefined;
  showArrow?: boolean;
  bottomSheetClassName?: string;
};

export const Tooltip: React.FC<TooltipProps> = React.memo(
  ({
    align = {},
    enabled = true,
    children = <div />,
    mouseEnterDelay = 0.3,
    mouseLeaveDelay = 0,
    overlay = '',
    overlayClassName = 'rc-tooltip--full-text',
    bottomSheetClassName = '',
    overlayTitle = '',
    placement = 'bottomLeft',
    trigger = defaultTrigger,
    id,
    visible: externalVisible,
    onVisibleChange,
    showArrow,
    ...rest
  }) => {
    const { isMobile } = useBowser();
    const externalVisibleIsPassed = useMemo(() => externalVisible !== undefined, [externalVisible]);

    const [internalVisible, setInternalVisible] = useState<boolean>(false);

    const onToggleVisible = useCallback(() => {
      if (externalVisibleIsPassed) {
        return onVisibleChange?.(!externalVisible);
      }

      setInternalVisible(enabled && !internalVisible);
    }, [enabled, externalVisible, externalVisibleIsPassed, internalVisible, onVisibleChange]);

    const onInternalVisible = useCallback(() => setInternalVisible(true), []);
    const offInternalVisible = useCallback(() => setInternalVisible(false), []);

    useEffect((): void => {
      if (!externalVisibleIsPassed) setInternalVisible(enabled && internalVisible);
    }, [enabled, externalVisibleIsPassed, internalVisible]);

    const overlayNode = useMemo(
      () =>
        typeof overlay === 'string' ? (
          <span dangerouslySetInnerHTML={{ __html: overlay }} />
        ) : (
          <Fragment>{overlay}</Fragment>
        ),
      [overlay],
    );

    const shouldOpenBottomSheet = useMemo(
      () => typeof overlay === 'string' && overlay.length > 100 && isMobile,
      [isMobile, overlay],
    );

    if (shouldOpenBottomSheet) {
      return (
        <>
          <span onClick={onInternalVisible}>{children}</span>
          <BottomSheet
            expandOnContentDrag
            className={classNames('light', bottomSheetClassName)}
            onDismiss={offInternalVisible}
            open={internalVisible}
          >
            <div className='rc-tooltip__sheet'>
              <div className='rc-tooltip__overlay-icon'>
                <SvgIconUrl className='rc-tooltip__overlay-svg' symbol={svgIcons.Info()} />
              </div>
              <span className='rc-tooltip__overlay-title H1_med_20'>{overlayTitle}</span>
              <span className='rc-tooltip__overlay-description Body3_reg_16'>{overlayNode}</span>
            </div>
          </BottomSheet>
        </>
      );
    }

    const overlayClassNames = classNames('rc-tooltip--full-text light', {
      [overlayClassName]: overlayClassName,
      'rc-tooltip--with-arrow': showArrow,
    });

    return (
      <RcTooltip
        align={align}
        destroyTooltipOnHide
        mouseEnterDelay={mouseEnterDelay}
        mouseLeaveDelay={mouseLeaveDelay}
        onVisibleChange={onToggleVisible}
        overlay={overlayNode}
        overlayClassName={overlayClassNames}
        placement={placement}
        trigger={trigger}
        visible={externalVisibleIsPassed ? externalVisible : internalVisible}
        id={id}
        {...rest}
      >
        {children}
      </RcTooltip>
    );
  },
);
